/**
 * Generic wrapper component for registering JSX instructions with InstructionContext.
 *
 * Renders children through the instruction reconciler and registers the whole
 * tree as one block (ordering controlled by block-level `priority`).
 * Renders nothing to the DOM itself.
 *
 * @example
 * ```tsx
 * <Instructions priority={10}>
 *   <tag-identity>You are Atlas, an AI assistant.</tag-identity>
 *   <ul>
 *     <li>Never fabricate data.</li>
 *   </ul>
 * </Instructions>
 * ```
 */

import type { ReactNode } from 'react';

import type { Placement } from './types.ts';
import { useRegisterInstructions } from './useRegisterInstructions.ts';

interface InstructionsProps {
  /** JSX instruction content (text, lists, `<tag-*>` wrappers, etc.). */
  children: ReactNode;
  /**
   * Render order across all registered blocks (ascending — lower renders first).
   * Defaults to 50. Applies to the entire block; content inside keeps document order.
   */
  priority?: number;
  /** Where to inject instructions ('system' or 'message'). Defaults to 'system'. */
  placement?: Placement;
}

/**
 * Wrapper component that registers a JSX instruction block with InstructionContext.
 *
 * The entire tree is treated as one ordered block — `priority` applies to the
 * whole block and content inside keeps its document order. Use `<tag-foo>`
 * children when you want XML wrapping around a sub-section, or omit them and
 * write raw prose/lists directly.
 *
 * Conditions can be handled with standard JSX — `{condition && ...}`, ternaries,
 * or array `.map()` — all are traversed transparently by the reconciler.
 */
export function Instructions({ children, priority = 50, placement = 'system' }: InstructionsProps) {
  useRegisterInstructions(children, { priority, placement });
  return null;
}
